import React, { useState, useEffect, useRef } from 'react';
import { CircularProgress, Alert, Snackbar } from '@mui/material';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { useTranslation } from 'react-i18next';
import BusinessList from './BusinessList';
import BusinessDetails from './BusinessDetails';
import AppointmentList from '../ownerDashboard/appointment/AppointmentList';
import { fetchBusinesses } from '../../lib/apiClientBusiness';
import { useAppointmentsContext } from '../../context/AppointmentsContext';
import { useStaffsContext } from '../../context/StaffsContext';
import { useServicesContext } from '../../context/ServicesContext';
import { useCustomersContext } from '../../context/CustomerContext';
import { useNotAvailableDateContext } from '../../context/NotAvailableDateContext';
import { useNotAvailableTimeContext } from '../../context/NotAvailableTimeContext';
//import { useOpeningHoursContext } from '../../context/OpeningHoursContext';
import { useWorkSessionsContext } from '../../context/WorkSessionsContext';
import { useCalculateMoneyContext } from '../../context/CalculateMoneyContext';
import CalculateMoneyView from '../CalculateMoney/CalculateMoneyView';
import CustomerInfo from '../customerInfo/CustomerInfo';
import { AnimatePresence, motion } from 'framer-motion';
import * as signalR from '@microsoft/signalr';
import {
  RootContainer,
  MainContainer,
  ContentContainer,
  StyledCard,
  LoadingContainer,
  CarouselIndicatorContainer,
  CarouselDot
} from '../../styles/OwnerStyle/OwnerDashboardStyles';
import { useNotificationsContext } from '../../context/NotificationsContext';

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;

const OwnerDashboard = () => {
  const { appointments, fetchAppointmentsForBusiness, setAppointments, fetchAppointmentById } = useAppointmentsContext();
  const { staff, fetchAllStaff } = useStaffsContext();
  const { customers, fetchCustomersForBusiness } = useCustomersContext();
  const { services, categories, fetchServices, fetchCategories } = useServicesContext();
  const { notAvailableDates, fetchAllNotAvailableDatesByBusiness } = useNotAvailableDateContext();
  const { notifications, fetchAllNotifications } = useNotificationsContext();
  const { notAvailableTimes, fetchAllNotAvailableTimesByBusiness } = useNotAvailableTimeContext();
  const { workSessions, fetchWorkSessionsByBusinessData } = useWorkSessionsContext();
  const {
    businessEarnings,
    getBusinessYearlyEarnings,
    getBusinessMonthlyEarnings,
    getBusinessWeeklyEarnings,
    getBusinessDailyEarnings,
    getBusinessTodaySummary
  } = useCalculateMoneyContext();

  const [businesses, setBusinesses] = useState([]);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeView, setActiveView] = useState('dashboard');
  const connectionRef = useRef(null);
  const [newNotificationMessage, setNewNotificationMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const { t } = useTranslation('ownerDashboard');

  function getISOWeek(date) {
    const target = new Date(date.valueOf());
    const dayNr = (date.getDay() + 6) % 7;
    target.setDate(target.getDate() - dayNr + 3);
    const firstThursday = new Date(target.getFullYear(), 0, 4);
    const diff = target - firstThursday;
    return 1 + Math.round((diff / 86400000 - 3 + ((firstThursday.getDay() + 6) % 7)) / 7);
  }

  const variants = {
    initial: { opacity: 0, x: 50 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 }
  };

  useEffect(() => {
    const loadBusinesses = async () => {
      try {
        const businessList = await fetchBusinesses();
        setBusinesses(businessList);

      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    loadBusinesses();
  }, []);

  useEffect(() => {
    const fetchAllData = async () => {
      if (!selectedBusiness?.businessId) return;
      setLoading(true);
      try {
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentWeek = getISOWeek(now);
        await Promise.all([
          fetchAppointmentsForBusiness(selectedBusiness.businessId),
          fetchAllStaff(selectedBusiness.businessId),
          fetchServices(selectedBusiness.businessId),
          fetchCategories(),
          fetchCustomersForBusiness(selectedBusiness.businessId),
          fetchAllNotAvailableDatesByBusiness(selectedBusiness.businessId),
          fetchAllNotAvailableTimesByBusiness(selectedBusiness.businessId),
          fetchAllNotifications(selectedBusiness.businessId),
          //fetchOpeningHoursForBusiness(selectedBusiness.businessId),
          fetchWorkSessionsByBusinessData(selectedBusiness.businessId),
          getBusinessWeeklyEarnings(selectedBusiness.businessId, currentYear, currentWeek),
          getBusinessTodaySummary(selectedBusiness.businessId)
        ]);
      } catch (error) {
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, [
    selectedBusiness,
    fetchAppointmentsForBusiness,
    fetchAllStaff,
    fetchServices,
    fetchCategories,
    fetchCustomersForBusiness,
    fetchAllNotAvailableDatesByBusiness,
    fetchAllNotAvailableTimesByBusiness,
    fetchAllNotifications,
    //fetchOpeningHoursForBusiness,
    fetchWorkSessionsByBusinessData,
    getBusinessWeeklyEarnings,
    getBusinessTodaySummary
  ]);

  // The SignalR handlers need the latest callbacks/state, but the connection must
  // not be torn down and rebuilt whenever one of them changes: any event pushed
  // during the gap between stop() and the new start() is lost forever. Keep the
  // latest values in a ref so the effect below can depend on the business only.
  const latestRef = useRef(null);
  latestRef.current = {
    activeView,
    fetchAppointmentsForBusiness,
    fetchAllStaff,
    fetchServices,
    fetchCustomersForBusiness,
    fetchAllNotAvailableDatesByBusiness,
    fetchAllNotAvailableTimesByBusiness,
    fetchAllNotifications,
    fetchWorkSessionsByBusinessData,
    getBusinessYearlyEarnings,
    getBusinessMonthlyEarnings,
    getBusinessWeeklyEarnings,
    getBusinessDailyEarnings,
    getBusinessTodaySummary,
    setAppointments,
    t
  };

  const selectedBusinessId = selectedBusiness?.businessId;

  // Setup SignalR connection — opened once per selected business.
  useEffect(() => {
    if (!selectedBusinessId) {
      return;
    }

    let disposed = false;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(`${apiBaseUrl}/appointmentHub`)
      .withAutomaticReconnect()
      .build();
    connectionRef.current = connection;

    // The hub broadcasts to every connected client, so only accept events for
    // the business currently open. Payloads without a businessId are kept.
    const belongsToSelectedBusiness = (businessId) =>
      businessId === undefined || businessId === null || String(businessId) === String(selectedBusinessId);

    const refreshAppointments = () =>
      latestRef.current.fetchAppointmentsForBusiness(selectedBusinessId);

    // Listen for appointment updates
    connection.on('ReceiveAppointmentUpdate', (data) => {
      const { appointment } = data || {};
      if (!appointment || !belongsToSelectedBusiness(appointment.businessId)) {
        return;
      }
      // Convert the services array to match the existing format
      const normalizedAppointment = {
        ...appointment,
        services: {
          $values: appointment.services || [] // Ensure services is wrapped in $values
        }
      };
      latestRef.current.setAppointments((prevAppointments) => {
        const appointmentExists = prevAppointments.some((appt) => appt.appointmentId === normalizedAppointment.appointmentId);
        if (appointmentExists) {
          return prevAppointments;
        }
        return [...prevAppointments, normalizedAppointment];
      });
    });

    // Listen for notification updates
    connection.on('ReceiveNotificationUpdate', async (message) => {
      await latestRef.current.fetchAllNotifications(selectedBusinessId); // Refresh the notifications
      setNewNotificationMessage(latestRef.current.t('notifications.newNotification')); // Set the message for the snackbar
      setSnackbarOpen(true);
    });

    // Listen for appointment deletion
    connection.on('ReceiveAppointmentDeleted', (appointmentId) => {
      latestRef.current.setAppointments(prevAppointments =>
        prevAppointments.filter(appt => appt.appointmentId !== appointmentId)  // Remove deleted appointment
      );
    });

    // Listen for appointment status change
    connection.on('ReceiveAppointmentStatusChanged', (appointmentUpdate) => {
      latestRef.current.setAppointments(prevAppointments =>
        prevAppointments.map(appt =>
          appt.appointmentId === appointmentUpdate.appointmentId
            ? { ...appt, status: appointmentUpdate.status }  // Update the status of the appointment
            : appt
        )
      );
    });

    // Listen for service updates
    connection.on('ReceiveServiceUpdate', async (message) => {
      await latestRef.current.fetchServices(selectedBusinessId); // Refresh the services
    });

    // Listen for staff updates
    connection.on('ReceiveStaffUpdate', async (message) => {
      await latestRef.current.fetchAllStaff(selectedBusinessId); // Refresh the staff
    });

    // Listen for customer updates
    connection.on('ReceiveCustomerUpdate', async (message) => {
      await latestRef.current.fetchCustomersForBusiness(selectedBusinessId); // Refresh the customers
    });

    // Listen for not available date updates
    connection.on('ReceiveNotAvailableDateUpdate', async (message) => {
      await latestRef.current.fetchAllNotAvailableDatesByBusiness(selectedBusinessId); // Refresh not available dates
    });

    // Listen for not available time updates
    connection.on('ReceiveNotAvailableTimeUpdate', async (message) => {
      await latestRef.current.fetchAllNotAvailableTimesByBusiness(selectedBusinessId); // Refresh not available times
    });

    // Listen for appointment deletion in customer
    connection.on('ReceiveAppointmentForCustomerDeleted', (appointmentId) => {
      latestRef.current.setAppointments(prevAppointments =>
        prevAppointments.filter(appt => appt.appointmentId !== appointmentId)  // Remove deleted appointment
      );
    });

    // Work session update
    ['ReceiveWorkSessionCreated', 'ReceiveWorkSessionUpdated', 'ReceiveWorkSessionDeleted'].forEach(event => {
      connection.on(event, async () => {
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth() + 1;
        const week = getISOWeek(now);
        const day = now.toISOString().split('T')[0]; // Format: YYYY-MM-DD

        await latestRef.current.fetchWorkSessionsByBusinessData(selectedBusinessId);

        if (latestRef.current.activeView === 'calculateMoney') {
          await Promise.all([
            latestRef.current.getBusinessYearlyEarnings(selectedBusinessId, year),
            latestRef.current.getBusinessMonthlyEarnings(selectedBusinessId, year, month),
            latestRef.current.getBusinessWeeklyEarnings(selectedBusinessId, year, week),
            latestRef.current.getBusinessDailyEarnings(selectedBusinessId, day),
            latestRef.current.getBusinessTodaySummary(selectedBusinessId)
          ]);
        }
      });
    });

    // Any event broadcast while the socket was down is gone, so resync on reconnect.
    connection.onreconnected(() => {
      if (!disposed) {
        refreshAppointments();
        latestRef.current.fetchAllNotifications(selectedBusinessId);
      }
    });

    const startConnection = async () => {
      try {
        await connection.start();
        if (disposed) {
          connection.stop().catch(() => { });
        }
      } catch (error) {
        // withAutomaticReconnect only kicks in after a successful start, so the
        // poll below is what retries a failed initial connection.
      }
    };

    startConnection();

    // Fallback so a dropped push self-heals instead of hiding the appointment
    // until a manual reload.
    const REFRESH_INTERVAL_MS = 60000;
    const intervalId = setInterval(() => {
      if (document.hidden) {
        return;
      }
      if (connection.state === signalR.HubConnectionState.Disconnected) {
        startConnection();
      }
      refreshAppointments();
    }, REFRESH_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshAppointments();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      disposed = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      connection.stop().catch(() => { });
      if (connectionRef.current === connection) {
        connectionRef.current = null;
      }
    };
  }, [selectedBusinessId]);

  const handleBusinessClick = (business) => {
    setSelectedBusiness(business);
  };

  // Snackbar close handler
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Change active view function
  const changeView = (view) => {
    setActiveView(view);
  };

  const handleBackToBusinessList = () => {
    setSelectedBusiness(null);
  };

  return (
    <RootContainer>
      <Navbar changeView={changeView} selectedBusiness={selectedBusiness} />
      {selectedBusiness && (
        <CarouselIndicatorContainer>
          {['dashboard', 'calculateMoney', 'customersInfo'].map((view) => (
            <CarouselDot
              key={view}
              onClick={() => changeView(view)}
              active={activeView === view}
            />
          ))}
        </CarouselIndicatorContainer>
      )}
      <MainContainer>
        <ContentContainer>
          <StyledCard>
            {loading ? (
              <LoadingContainer>
                <CircularProgress />
              </LoadingContainer>
            ) : error ? (
              <Alert severity="error">{error}</Alert>
            ) : selectedBusiness ? (
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeView}
                  variants={variants}
                  initial="initial"
                  animate="animate"
                  exit="exit"
                  transition={{ duration: 0.25 }}
                  style={{ width: '100%', height: '100%' }}
                >
                  {activeView === 'dashboard' && (
                    <>
                      <BusinessDetails
                        selectedBusiness={selectedBusiness}
                        setSelectedBusiness={setSelectedBusiness}
                        staff={staff}
                        services={services}
                        categories={categories}
                        appointments={appointments}
                        customers={customers}
                        notAvailableDates={notAvailableDates}
                        notAvailableTimes={notAvailableTimes}
                        notifications={notifications}
                        fetchAppointmentById={fetchAppointmentById}
                        //openingHours={openingHours}
                        onBack={handleBackToBusinessList}
                      />
                      <AppointmentList
                        appointments={appointments}
                        staff={staff}
                        services={services}
                        businessId={selectedBusiness.businessId}
                        fetchAppointmentById={fetchAppointmentById}
                      />
                    </>
                  )}
                  {activeView === 'calculateMoney' && (
                    <CalculateMoneyView
                      businessId={selectedBusiness.businessId}
                      workSessions={workSessions}
                      earningsSummary={businessEarnings}
                      staff={staff}
                      currentWeek={getISOWeek(new Date())}
                      services={services}
                      categories={categories}
                    />
                  )}
                  {activeView === 'customersInfo' && (
                    <CustomerInfo
                      customers={customers || []}
                      businessId={selectedBusiness?.businessId}
                      businessName={selectedBusiness?.name}
                    />
                  )}
                </motion.div>
              </AnimatePresence>
            ) : (
              <BusinessList businesses={businesses} onBusinessClick={handleBusinessClick} />
            )}
          </StyledCard>
        </ContentContainer>
      </MainContainer>
      <Footer />
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        open={snackbarOpen}
        onClose={handleSnackbarClose}
        message={newNotificationMessage}
        autoHideDuration={10000}
      />
    </RootContainer>
  );
};

export default OwnerDashboard;